import type { FormEvent } from 'react';
import { createRef } from 'react';
import ReCaptcha from 'react-google-recaptcha';
import { useLocation, useSearchParams } from 'react-router';
import { trackEvent } from '@gonfalon/analytics-events/trackEvent';
import { googleCaptchaSiteKey } from '@gonfalon/constants/constants';
import {
  enableGDPRComplianceCheckboxes,
  enableRemoveOrganizationFromSignup,
  enableSignupRefererForwarding,
  isCaptchaEnabled,
  newAccountTrialDurationDays,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { useTypedAppForm } from '@gonfalon/forms/hooks';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { getDemoRequestUrl } from '@gonfalon/onboarding/utils';
import { useCreateUnverifiedMember } from '@gonfalon/rest-api/queries/account';
import { buildSignupCustomReferer, getForwardedReferer } from '@gonfalon/signup/agentUtmParams';
import { getSignupTrialSubmitAnalyticsProps } from '@gonfalon/signup/analytics';
import { BrandArrow } from '@gonfalon/ui/BrandArrow';
import { NewTabLink } from '@gonfalon/ui/NewTabLink';
import { Alert, Button, Form, Label, Link } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { z } from 'zod';

import { useRequiresGDPRCompliance } from '#hooks/useRequiresGDPRCompliance';
import { logger } from '#utils/flagExperimentUtils';
import { trackGDPRUserOptOut, trackSignUp } from '#utils/loginUtils';
import { Logger } from '#utils/logUtils';
import { clearStoredSignupLandingUrl } from '#utils/signupLandingUrlStorage';

import { useSignUpHandlers } from './useSignUpHandlers';

import styles from './SignUpForm.module.css';
import layoutStyles from '@gonfalon/signup/signUpFormLayout.module.css';

const signupLogger = Logger.get('SignUpForm');

type SignUpFormProps = {
  hideDemoLink?: boolean;
  /** Trial length quoted on the submit button. */
  trialDurationDays?: number;
};

export const SignUpForm = ({
  hideDemoLink = false,
  trialDurationDays = newAccountTrialDurationDays(),
}: SignUpFormProps) => {
  const { requiresGDPRCompliance } = useRequiresGDPRCompliance();
  const enableGDPRCompliance = enableGDPRComplianceCheckboxes() && requiresGDPRCompliance;
  const onSuccessHandler = useSignUpHandlers();
  const createUnverifiedMember = useCreateUnverifiedMember();
  const showCaptcha = isCaptchaEnabled();
  const recaptchaRef = createRef<ReCaptcha>();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const { mutate, isError, error, isPending } = createUnverifiedMember;

  const requiredField = z.string().min(1, 'Error: This field is required');
  const FormSchema = z.object({
    firstName: requiredField,
    lastName: requiredField,
    username: requiredField.email('Error: This is not a valid email address'),
    password: z
      .string()
      .min(8, 'Error: This must be at least 8 characters')
      .refine(
        (val) => {
          const checks = [
            /[A-Z]/.test(val), // uppercase
            /[a-z]/.test(val), // lowercase
            /[0-9]/.test(val), // number
            /[^A-Za-z0-9]/.test(val), // special character
          ];
          return checks.filter(Boolean).length >= 3;
        },
        {
          message:
            'Error: This must contain at least three of the following: 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character',
        },
      ),
    organization: enableRemoveOrganizationFromSignup() ? z.string().optional() : requiredField,
    marketingEmails: z.boolean().optional(),
    acceptTerms: enableGDPRCompliance
      ? z.boolean().refine((value) => value, { error: () => msg('signUp.signUpForm.termsRequiredError') })
      : z.boolean().optional(),
  });

  type FormData = z.infer<typeof FormSchema>;

  const submitForm = (data: FormData) => {
    const customReferer = enableSignupRefererForwarding()
      ? getForwardedReferer(searchParams)
      : buildSignupCustomReferer(searchParams);

    const body: FormData & { customReferer: string } = { ...data, customReferer };
    signupLogger.log(`signup: submitting form with customReferer (${customReferer})`);
    const payload = { body };

    if (enableGDPRCompliance && !data.marketingEmails) {
      trackGDPRUserOptOut({ component: 'SignUpForm', email: data.username });
    }
    trackEvent('App Signup Form Submitted', getSignupTrialSubmitAnalyticsProps());
    mutate(payload, {
      onSuccess: async (response) => {
        clearStoredSignupLandingUrl();
        await onSuccessHandler({
          ...data,
          signupOutcome: response.signupOutcome,
          redirectUrl: response.redirectUrl,
        });
      },
      onError: (err) => {
        logger.error(`Error creating unverified member: ${err}`);
      },
    });
  };

  const onSubmit = ({ value }: { value: FormData }) => {
    if (showCaptcha) {
      if (!recaptchaRef.current) {
        return;
      }

      recaptchaRef.current.reset();
      recaptchaRef.current.execute();
    } else {
      submitForm(value);
    }
  };

  const form = useTypedAppForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      organization: undefined,
      acceptTerms: false,
      marketingEmails: false,
      password: '',
    },
    validators: {
      onChange: FormSchema,
      onSubmit: FormSchema,
      onMount: FormSchema,
    },
    onSubmit,
  });

  const { AppForm, AppField, Subscribe } = form;

  const handleCaptchaChange = (value: string | null) => {
    const { values } = form.state;
    const formWithCaptcha = { ...values, captcha: value || '' };

    submitForm(formWithCaptcha);
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    void form.handleSubmit();
  };

  return (
    <Form onSubmit={handleFormSubmit} className={layoutStyles.form}>
      <AppForm>
        {isError && <Alert status="error">{error?.message || msg('signUp.signUpForm.submitError')}</Alert>}
        <div className={layoutStyles.name}>
          <div>
            <AppField name="firstName">
              {(field) => <field.TextInputField label={msg('signUp.signUpForm.firstNameLabel')} isRequired />}
            </AppField>
          </div>
          <div>
            <AppField name="lastName">
              {(field) => <field.TextInputField label={msg('signUp.signUpForm.lastNameLabel')} isRequired />}
            </AppField>
          </div>
        </div>
        <div className={layoutStyles.username}>
          <AppField name="username">
            {(field) => <field.TextInputField label={msg('signUp.signUpForm.emailLabel')} type="email" isRequired />}
          </AppField>
        </div>
        {!enableRemoveOrganizationFromSignup() && (
          <div className={styles.organization}>
            <AppField name="organization">
              {(field) => (
                <field.TextInputField label={msg('signUp.signUpForm.organizationLabel')} name="organization" />
              )}
            </AppField>
          </div>
        )}
        <div className={styles.password}>
          <AppField name="password">
            {(field) => (
              <field.TextInputField
                label={msg('signUp.signUpForm.passwordLabel')}
                type="password"
                isRequired
                autoComplete="new-password"
              />
            )}
          </AppField>
        </div>
        {enableGDPRCompliance && (
          <div className={styles.checkboxes}>
            <AppField name="acceptTerms">
              {(field) => (
                <field.Checkbox>
                  <Label>
                    <Message id="signUp.signUpForm.acceptTerms" />{' '}
                    <NewTabLink
                      href="https://launchdarkly.com/policies/subscription-terms/"
                      text={msg('signUp.signUpForm.termsLink')}
                      showIcon={false}
                    />
                  </Label>
                </field.Checkbox>
              )}
            </AppField>
            <AppField name="marketingEmails">
              {(field) => (
                <field.Checkbox>
                  <Label>
                    <Message id="signUp.signUpForm.marketingOptIn" />
                  </Label>
                </field.Checkbox>
              )}
            </AppField>
          </div>
        )}
        <div className={layoutStyles.submit}>
          <Subscribe
            selector={(state) => {
              return {
                isValid: state.isValid,
                isSubmitting: state.isSubmitting,
              };
            }}
          >
            {({ isValid, isSubmitting }: { isValid: boolean; isSubmitting: boolean }) => (
              <Button
                variant="primary"
                isDisabled={!isValid || isPending || isSubmitting}
                className={layoutStyles.submitButton}
                type="submit"
                isPending={isPending || isSubmitting}
              >
                {isPending || isSubmitting
                  ? msg('signUp.signUpForm.submitting')
                  : msg('signUp.signUpForm.submit', { days: trialDurationDays })}
                <BrandArrow />
              </Button>
            )}
          </Subscribe>
          {!hideDemoLink && (
            <>
              {msg('signUp.signUpForm.demoLinkPrefix')}
              <Link
                href={getDemoRequestUrl()}
                className={styles.demoLink}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  trackSignUp('CTA Link Clicked', {
                    component: 'SignUpForm',
                    ctaText: 'Get a live demo',
                    url: pathname,
                  })
                }
              >
                {msg('signUp.signUpForm.demoLink')}
                <Icon className={styles.arrowIcon} name="arrow-right-thin" />
              </Link>
            </>
          )}
        </div>
        {showCaptcha && (
          <ReCaptcha
            ref={recaptchaRef}
            sitekey={googleCaptchaSiteKey()}
            size="invisible"
            onChange={handleCaptchaChange}
          />
        )}
      </AppForm>
    </Form>
  );
};
